import { alpha, Badge, Box, Button, CircularProgress, IconButton, MenuItem, OutlinedInput, Select, Stack, Tabs, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { enqueueSnackbar } from "notistack";
import DisplayErrors from "src/components/display-errors";
import Iconify from "src/components/iconify/iconify";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxTextField from "src/components/inputs/trux-text-field";
import SimpleDialog from "src/components/simple-dialog";
import { ModelSavingData } from "src/hooks/use-model-saving-data";
import { EldInspection } from "src/models";
import { useDropzone } from "react-dropzone";
import axios, { endpoints } from "src/utils/axios";

type EldInspectionModalProps = {
  selectedEldInspectionModel: ModelSavingData<EldInspection>,
  isOpen: boolean,
  setIsOpen: Dispatch<SetStateAction<boolean>>,
  updateEldInspection: VoidFunction,
}

export function EldInspectionModal(
  { selectedEldInspectionModel, isOpen, setIsOpen, updateEldInspection }: EldInspectionModalProps) {

  const [savingFile, setSavingFile] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach(file => {
        setSavingFile(true)

        // here we need to upload the file
        axios.postForm(
          endpoints.eldInspections.upload,
          { file }
        ).then(response => response.data).then((response) => {
          if (response.fileUrl) {
            enqueueSnackbar("Successfully uploaded the File", { variant: "success" });

            selectedEldInspectionModel.update(eldInspection => {
              eldInspection.attachedFileUrl = response.fileUrl;
            });
          }
        }).catch(() => {
          enqueueSnackbar("Your file could not be uploaded. Please try again.", { variant: "error" });
        }).finally(() => {
          setSavingFile(false);
        })
      })
    }, [selectedEldInspectionModel]);

  const onRemoveFile = useCallback(() => {
    selectedEldInspectionModel.update(eldInspection => {
      eldInspection.attachedFileUrl = "";
    })
  }, [selectedEldInspectionModel]);

  const {
    getRootProps,
    getInputProps,
    isDragActive
  } = useDropzone({
    onDrop, accept: {
      'image/png': ['.png'],
      'image/jpg': ['.jpg'],
      'application/pdf': ['.pdf'],
    }
  });

  return selectedEldInspectionModel.model && 
  <SimpleDialog
    title={"Add Comment and File to Inspection"}
    open={isOpen}
    size="medium"
    onCancel={() => {
      setIsOpen(false);
    }}
    onSubmit={updateEldInspection}
    submitDisabled={selectedEldInspectionModel.loading}
    submitButton="Update Inspection"
    submitButtonIcon={<Iconify icon="basil:save-solid" />}
    submitButtonColor= "info">


    <DisplayErrors savingData={selectedEldInspectionModel} />

    <Stack direction="column" gap={1}>

      <TruxFormField label="User Comment">
        <TruxTextField modelSavingData={selectedEldInspectionModel} field="userComment" />
      </TruxFormField>
      <TruxFormField label="Attach File">
        <>
          {savingFile && <CircularProgress />}
          {!savingFile && selectedEldInspectionModel.model?.attachedFileUrl && 
            <Stack direction="row" gap={2}>
              <Box maxWidth="200px">
                {selectedEldInspectionModel.model?.attachedFileUrl.endsWith('pdf') ? 
                  <a href={selectedEldInspectionModel.model?.attachedFileUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Iconify icon="proicons:pdf-2" width={100} style={{ color: "black" }}/>
                  </a> : 
                  <img src={selectedEldInspectionModel.model?.attachedFileUrl} alt="Invoice Logo" />}
              </Box>
              <Box>
                <IconButton color="error" onClick={onRemoveFile} >
                  <Iconify icon="solar:trash-bin-trash-bold" />
                </IconButton>
              </Box>
            </Stack>
          }
          {!savingFile && !selectedEldInspectionModel.model?.attachedFileUrl && 
            <Box>
              <Box {...getRootProps()} onFocus={null as any} onBlur={null as any}>
                <input {...getInputProps()} />
                <Box sx={{
                    m: 0.5,
                    flexShrink: 0,
                    flexGrow: 1,
                    height: "80px",
                    width: "120px",
                    borderRadius: 1,
                    cursor: 'pointer',
                    alignItems: 'center',
                    color: 'text.disabled',
                    justifyContent: 'center',
                    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
                    border: (theme) => `dashed 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                    ...(isDragActive && {
                      opacity: 0.72,
                    }),
                    '&:hover': {
                      opacity: 0.72,
                    }
                }}>
                  <Stack spacing={0.5} paddingTop={1} alignItems="center">
                    <Iconify icon="eva:cloud-upload-fill" width={40} />
                    <Typography variant="body2">
                      Upload File
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Box>
          }
        </>
      </TruxFormField>
    </Stack>
  </SimpleDialog>
}

